import {
  Button,
  ErrorMessage,
  Header,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { getCookie, trackError } from '@vend/utilities'
import classNames from 'classnames'
import React, { FormEvent, useEffect, useState } from 'react'
import {
  PAGE_ROUTE,
  useTranslateValidationMessage,
  VALIDATION_MESSAGES,
  VendDomainSuffix,
} from '../../../../constants/constants'
import { checkDomainPrefix } from '../../../../utils/api'
import { useDisplayLSIDCallbackError } from '../../../../utils/lsidCallbackError'

interface ConfirmStoreProps {
  domainSuffix: VendDomainSuffix
}

export const ConfirmStore: React.FC<
  React.PropsWithChildren<ConfirmStoreProps>
> = ({ domainSuffix }) => {
  const t = useT()
  const translateValidation = useTranslateValidationMessage()

  useDisplayLSIDCallbackError()

  // Get search params from url to carry over to sign in page
  const rawURLSearchQuery = window.location.search
  const urlSearchQuery = new URLSearchParams(rawURLSearchQuery)

  // During the OAuth flow users are first redirected to secure.retail.lightspeed.app/connect
  // That endpoint is handled by bouncer. But because bouncer doesn't know which domain it is for
  // it will redirected to this endpoint, the /signin on secure, with the OAuth pieces in the return url.
  // Once the user is properly marked as authenticated we can send them back to bouncer and the OAuth
  // process will start. However, in some cases we might want to skip the secure subdomain because we
  // know the user is signed in to a specific domain from the `store` cookie. We do this by adding
  // an extra param `skip_confirm_domain` to the oauth request.
  const returnParam = urlSearchQuery.get('return') ?? ''
  let isAutoSubmit = false
  if (returnParam && returnParam.includes('?')) {
    const returnURLParams = new URLSearchParams(returnParam.split('?')[1])
    isAutoSubmit = returnURLParams.get('skip_confirm_domain') === 'true'
  }

  const SIGNIN_URL = `${domainSuffix}${PAGE_ROUTE.signin}`

  const domain = getCookie('store')?.split('|')[1] || ''
  const [domainPrefix, setDomainPrefix] = useState(domain)
  const [inputErrorMessage, setInputErrorMessage] = useState('')
  const [storeName, setStoreName] = useState('')

  // Read authenticated domain prefix from cookie and get the store name.
  useEffect(() => {
    if (!domain) {
      return
    }
    checkDomainPrefix(domain)
      .then(({ valid, store_name: store }) => {
        if (valid && store) {
          setStoreName(store)

          if (isAutoSubmit) {
            window.location.assign(
              `https://${domainPrefix}${SIGNIN_URL}${rawURLSearchQuery}`
            )
          }
        }
      })
      .catch(error => {
        trackError(error)
      })
    // We only want this to run once on component mount with the values it has at the time.
    // We explicitly don't want to run this check when values are update as the user will manually
    // click submit to check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateForm = () => {
    if (!domainPrefix) {
      setInputErrorMessage(
        translateValidation(VALIDATION_MESSAGES.domainPrefixRequired)
      )
      return false
    }

    setInputErrorMessage('')
    return true
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      ToastNotifications.negative(
        translateValidation(VALIDATION_MESSAGES.fieldsRequired)
      )
      return
    }
    checkDomainPrefix(domainPrefix)
      .then(({ valid, store_name: store }) => {
        if (valid && store) {
          window.location.assign(
            `https://${domainPrefix}${SIGNIN_URL}${rawURLSearchQuery}`
          )
        } else {
          setInputErrorMessage(
            translateValidation(VALIDATION_MESSAGES.domainPrefixInvalid)
          )
        }
      })
      .catch(error => {
        trackError(error)
        ToastNotifications.negative(
          translateValidation(VALIDATION_MESSAGES.generic)
        )
      })
  }

  return (
    <Modal focusTrapActive={false} pageModal size="small">
      <ModalHeader>
        <Header level="3" type="heading">
          {t('Log in to Lightspeed Retail (X-Series)')}
        </Header>
      </ModalHeader>
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <div className="vr-store-field-wrap">
            <div className="vd-field vr-store-field">
              <div className="vd-label">{t('Store URL')}</div>
              <div className="vd-value">
                <input
                  className={classNames('vd-input', {
                    'vd-error': Boolean(inputErrorMessage),
                  })}
                  value={domainPrefix}
                  name="domainPrefix"
                  autoCorrect="off"
                  autoFocus
                  autoCapitalize="off"
                  onChange={e => {
                    setDomainPrefix(e.target.value)
                  }}
                  placeholder={t('Enter your store URL')}
                />
              </div>
            </div>
            <label className="vd-ml4 vr-input-suffix">{domainSuffix}</label>
          </div>

          {inputErrorMessage && (
            <ErrorMessage> {inputErrorMessage} </ErrorMessage>
          )}

          <div
            className={classNames(
              'vd-field vd-flex vd-flex--align-center vd-mt5 vr-pannel-footer vr-btn-wrap',
              {
                'vd-flex--justify-between': storeName,
                'vd-flex--justify-end': !storeName,
              }
            )}
          >
            {storeName ? (
              <p className="vd-text-body">
                <span>
                  <T
                    _str="You’re already signed in to {storeName}"
                    storeName={
                      <Link
                        data-track="switch-store"
                        href={`https://${domainPrefix}${SIGNIN_URL}${rawURLSearchQuery}`}
                        className="vd-ml1 vd-mr3"
                        isSecondary
                      >
                        {storeName}
                      </Link>
                    }
                  />
                </span>
              </p>
            ) : null}
            <Button data-track="signin-lightspeed-retail" type="submit">
              {t('Next')}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  )
}
